/**
 * EnemyShips — the FIGHTER, a hostile raider (its own dark/red hull sprite).
 * Mk II-class ship with 1–2 FORWARD-firing twin red turrets.
 *
 * Behaviour:
 *   • Not aggro → ROAM the map: fly to random waypoints (never circling), at a
 *     modest speed, steering around asteroids.
 *   • Aggro → fast DOGFIGHT: charge head-on firing forward; once it gets close it
 *     commits to a straight fly-PAST for ~2s (blowing past the player), then
 *     curves back around for another attack run. This repeats.
 *
 * EnemyShip satisfies the turret system's `Damageable` shape, so the player's
 * turrets target and damage fighters directly.
 */
import { Vector2 } from "@/math/Vector2";
import { ENEMY } from "@/config/balance";
import type { MineableBody } from "@/game/world/body";

export interface EnemyBullet {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
}

export interface EnemyTurret {
  angle: number; // aim — always locked to the hull heading (forward only)
  cooldown: number;
  flash: number; // muzzle flash 0..1
}

export type EnemyMode = "run" | "straight" | "warpin" | "arrive" | "launch";

// Jump-drive reinforcements: streak in at 1000% speed, hold a beat, then engage.
const WARP_SPEED_MUL = 10;
const WARP_HOLD = 1; // seconds held at the arrival point before aggro
const WARP_GAP = 46; // formation spacing
const WARP_CORNER_DIST = 2600; // spawn this far out (off-screen corner)

export interface EnemyShip {
  pos: Vector2;
  vel: Vector2;
  heading: number;
  hp: number;
  maxHp: number;
  shield: number;
  maxShield: number;
  shieldFlash: number;
  hitTimer: number;
  turretCount: number; // 1 or 2 equipped turrets
  turrets: EnemyTurret[]; // always length 2; only `turretCount` are live
  aggro: boolean;
  mode: EnemyMode; // dogfight state
  straightTimer: number; // seconds left in the current straight fly-past
  roam: Vector2; // current patrol waypoint (solo fighters)
  squad: number; // squadron index, or -1 for a lone fighter
  respawnTimer: number; // >0 while dead, counting down to respawn
  warpTarget: Vector2 | null; // arrival point when warping in
  holdTimer: number; // >0 while holding at the arrival point (also the launch fly-out timer)
  targetAlly: { pos: { x: number; y: number }; hp: number } | null; // the escort fighter it's hunting (null = mothership)
  noRespawn?: boolean; // true for a Carrier's hangar fighters — once dead they stay dead
}

export interface EnemyCtx {
  playerX: number;
  playerY: number;
  playerRadius: number;
  playerAlive: boolean;
  allies: { pos: { x: number; y: number }; hp: number }[]; // escort fighters — hunted first
  bodies: MineableBody[]; // asteroids/moons to steer around
  onHitPlayer: (dmg: number, x: number, y: number) => void;
  onDeath: (x: number, y: number) => void;
}

function makeTurret(seedAngle: number): EnemyTurret {
  return { angle: seedAngle, cooldown: Math.random() * ENEMY.fireInterval, flash: 0 };
}

function norm(a: number): number {
  while (a > Math.PI) a -= Math.PI * 2;
  while (a < -Math.PI) a += Math.PI * 2;
  return a;
}

/** A random point somewhere across the field for roaming. */
function randomWaypoint(): Vector2 {
  const a = Math.random() * Math.PI * 2;
  const d = 250 + Math.random() * 1500;
  return new Vector2(Math.cos(a) * d, Math.sin(a) * d);
}

export class EnemyManager {
  readonly ships: EnemyShip[] = [];
  readonly bullets: EnemyBullet[] = [];
  private readonly spawn: { x: number; y: number };
  private readonly squadRoams: Vector2[] = []; // shared waypoint per squadron
  private noMoreRespawns = false; // set when the Eater of Worlds awakens → no respawns

  constructor(
    count: number,
    spawn: { x: number; y: number } = { x: 0, y: 0 },
    squadron = false,
  ) {
    this.spawn = { x: spawn.x, y: spawn.y };
    for (let i = 0; i < count; i++) this.ships.push(this.make());
    if (squadron) this.formSquadrons();
  }

  /** Stop all further respawns (existing ships stay; dead ones don't come back). */
  haltRespawns(): void {
    this.noMoreRespawns = true;
  }

  /** Group fighters into squadrons of 3–5 that spawn clustered and share a roam
   * waypoint, so they fly together (but still fight independently). */
  private formSquadrons(): void {
    let sq = 0;
    let i = 0;
    while (i < this.ships.length) {
      const size = 3 + Math.floor(Math.random() * 3); // 3..5
      const center = this.spawnPos(); // clear of the player's entry point
      this.squadRoams[sq] = randomWaypoint();
      for (let k = 0; k < size && i < this.ships.length; k++, i++) {
        const sh = this.ships[i]!;
        sh.squad = sq;
        sh.pos.set(
          center.x + (Math.random() * 2 - 1) * 130,
          center.y + (Math.random() * 2 - 1) * 130,
        );
        sh.heading = Math.atan2(-sh.pos.y, -sh.pos.x);
      }
      sq += 1;
    }
  }

  /** A spawn position out at the edge, kept clear of the player's entry point. */
  private spawnPos(): Vector2 {
    for (let tries = 0; tries < 10; tries++) {
      const a = Math.random() * Math.PI * 2;
      const d = 820 + Math.random() * 700;
      const x = Math.cos(a) * d;
      const y = Math.sin(a) * d;
      const dx = x - this.spawn.x;
      const dy = y - this.spawn.y;
      if (dx * dx + dy * dy >= ENEMY.minSpawnDist * ENEMY.minSpawnDist) {
        return new Vector2(x, y);
      }
    }
    // Fallback: opposite side of the map from the player's entry point.
    return new Vector2(-this.spawn.x, -this.spawn.y - 900);
  }

  private make(): EnemyShip {
    const pos = this.spawnPos();
    const heading = Math.atan2(-pos.y, -pos.x); // face roughly inward
    return {
      pos,
      vel: new Vector2(),
      heading,
      hp: ENEMY.maxHp,
      maxHp: ENEMY.maxHp,
      shield: ENEMY.maxShield,
      maxShield: ENEMY.maxShield,
      shieldFlash: 0,
      hitTimer: 999,
      turretCount: Math.random() < 0.5 ? 1 : 2,
      turrets: [makeTurret(heading), makeTurret(heading)],
      aggro: false,
      mode: "run",
      straightTimer: 0,
      roam: randomWaypoint(),
      squad: -1,
      respawnTimer: 0,
      warpTarget: null,
      holdTimer: 0,
      targetAlly: null,
    };
  }

  /** Jump-drive reinforcements: `count` fighters streak in from a far corner in
   * squadron formation to a line beside `arrival`, hold, then aggro. */
  warpInSquad(count: number, arrival: { x: number; y: number }): void {
    if (this.noMoreRespawns) return; // no reinforcements once the Eater has awoken
    const cornerA = Math.random() * Math.PI * 2;
    const cx = Math.cos(cornerA) * WARP_CORNER_DIST;
    const cy = Math.sin(cornerA) * WARP_CORNER_DIST;
    const ang = Math.atan2(arrival.y - cy, arrival.x - cx);
    const px = -Math.sin(ang);
    const py = Math.cos(ang);
    for (let i = 0; i < count; i++) {
      const off = (i - (count - 1) / 2) * WARP_GAP;
      const sh = this.make();
      sh.pos.set(cx + px * off, cy + py * off);
      sh.warpTarget = new Vector2(arrival.x + px * off, arrival.y + py * off);
      sh.heading = ang;
      sh.mode = "warpin";
      sh.aggro = false;
      sh.holdTimer = 0;
      sh.squad = -1;
      this.ships.push(sh);
    }
  }

  /** Launch a single fighter out of a Carrier's hangar bay: it appears at the bay
   * mouth, flies straight out along `heading` for `straightTime` seconds, then
   * engages. These fighters are ONE-SHOT — flagged noRespawn so they never come back. */
  launchFighter(gate: { x: number; y: number }, heading: number, straightTime: number): void {
    if (this.noMoreRespawns) return; // no new hostiles once the Eater has awoken
    const sh = this.make();
    sh.pos.set(gate.x, gate.y);
    sh.heading = heading;
    sh.vel.set(Math.cos(heading) * ENEMY.speed, Math.sin(heading) * ENEMY.speed);
    sh.mode = "launch";
    sh.holdTimer = straightTime; // reuse holdTimer as the straight fly-out countdown
    sh.aggro = false;
    sh.squad = -1;
    sh.noRespawn = true;
    for (const t of sh.turrets) t.angle = heading;
    this.ships.push(sh);
  }

  update(dt: number, ctx: EnemyCtx): void {
    for (const s of this.ships) this.updateShip(s, dt, ctx);

    // Advance red bolts; a strike on the player deals damage.
    for (let i = this.bullets.length - 1; i >= 0; i--) {
      const b = this.bullets[i]!;
      b.x += b.vx * dt;
      b.y += b.vy * dt;
      b.life -= dt;
      let hit = false;
      if (ctx.playerAlive) {
        const rr = ctx.playerRadius + 5;
        if ((ctx.playerX - b.x) ** 2 + (ctx.playerY - b.y) ** 2 < rr * rr) {
          ctx.onHitPlayer(ENEMY.boltDamage, b.x, b.y);
          hit = true;
        }
      }
      if (hit || b.life <= 0) this.bullets.splice(i, 1);
    }
  }

  private updateShip(s: EnemyShip, dt: number, ctx: EnemyCtx): void {
    // Destroyed → explode once, then either a fresh fighter warps in at the edge, or
    // (a Carrier's one-shot hangar fighter) it stays dead for good.
    if (s.hp <= 0) {
      if (s.respawnTimer <= 0) {
        ctx.onDeath(s.pos.x, s.pos.y);
        s.respawnTimer = ENEMY.respawnDelay;
      }
      if (s.noRespawn || this.noMoreRespawns) return; // one-shot / boss halt — no respawn
      s.respawnTimer -= dt;
      if (s.respawnTimer <= 0) this.respawn(s);
      return;
    }

    s.shieldFlash = Math.max(0, s.shieldFlash - dt * 3);
    s.hitTimer += dt;
    for (const t of s.turrets) {
      t.flash = Math.max(0, t.flash - dt * 5);
      t.cooldown -= dt;
    }

    // Jump-drive arrival: streak to the formation point at 1000% speed…
    if (s.mode === "warpin" && s.warpTarget) {
      const wx = s.warpTarget.x - s.pos.x;
      const wy = s.warpTarget.y - s.pos.y;
      const wd = Math.hypot(wx, wy) || 1;
      const spd = ENEMY.speed * WARP_SPEED_MUL;
      s.heading = Math.atan2(wy, wx);
      if (wd <= spd * dt || wd < 30) {
        s.pos.set(s.warpTarget.x, s.warpTarget.y);
        s.vel.set(0, 0);
        s.mode = "arrive";
        s.holdTimer = WARP_HOLD;
      } else {
        s.vel.set((wx / wd) * spd, (wy / wd) * spd);
        s.pos.x += s.vel.x * dt;
        s.pos.y += s.vel.y * dt;
      }
      for (const t of s.turrets) t.angle = s.heading;
      return;
    }
    // …hold beside the cruiser for a beat, then engage the player.
    if (s.mode === "arrive") {
      s.vel.set(0, 0);
      s.heading = Math.atan2(ctx.playerY - s.pos.y, ctx.playerX - s.pos.x);
      for (const t of s.turrets) t.angle = s.heading;
      s.holdTimer -= dt;
      if (s.holdTimer <= 0) {
        s.mode = "run";
        s.aggro = true;
        s.warpTarget = null;
      }
      return;
    }
    // Carrier hangar launch: fly straight out of the bay for a beat (guns forward),
    // then flip into a normal aggro dogfight.
    if (s.mode === "launch") {
      s.vel.set(Math.cos(s.heading) * ENEMY.speed, Math.sin(s.heading) * ENEMY.speed);
      s.pos.x += s.vel.x * dt;
      s.pos.y += s.vel.y * dt;
      for (const t of s.turrets) t.angle = s.heading;
      s.holdTimer -= dt;
      if (s.holdTimer <= 0) {
        s.mode = "run";
        s.aggro = true;
      }
      return;
    }

    // Hunt the nearest live escort fighter first (fight the fighters before the
    // mothership); fall back to the mothership when none are close.
    let tx = ctx.playerX;
    let ty = ctx.playerY;
    let targetAlive = ctx.playerAlive;
    let chosenAlly: EnemyCtx["allies"][number] | null = null;
    let nd = (ENEMY.aggroRange * 1.5) ** 2;
    for (const a of ctx.allies) {
      if (a.hp <= 0) continue;
      const d = (a.pos.x - s.pos.x) ** 2 + (a.pos.y - s.pos.y) ** 2;
      if (d < nd) {
        nd = d;
        tx = a.pos.x;
        ty = a.pos.y;
        targetAlive = true;
        chosenAlly = a;
      }
    }
    s.targetAlly = chosenAlly; // so our fighters can turn and fight whoever is hunting them
    const dx = tx - s.pos.x;
    const dy = ty - s.pos.y;
    const dist = Math.hypot(dx, dy) || 1e-3;
    const toPlayer = Math.atan2(dy, dx);
    if (dist < ENEMY.aggroRange) s.aggro = true;
    else if (dist > ENEMY.aggroRange * 1.6) s.aggro = false;

    let desired: number;
    let speed: number;
    let firing = false;

    if (s.aggro && targetAlive) {
      if (s.mode === "run") {
        // Fast head-on attack run, guns forward.
        desired = toPlayer;
        speed = ENEMY.speed;
        firing = Math.abs(norm(toPlayer - s.heading)) < ENEMY.fireCone;
        // Once close, commit to blowing straight past the player.
        if (dist < ENEMY.passRange) {
          s.mode = "straight";
          s.straightTimer = ENEMY.straightTime;
        }
      } else {
        // Fly straight past for a beat (keep current heading), then curve back.
        // +30% speed burst for the first second so it breaks away fast.
        desired = s.heading;
        const boosting = s.straightTimer > ENEMY.straightTime - 1;
        speed = ENEMY.speed * (boosting ? 1.3 : 1);
        s.straightTimer -= dt;
        if (s.straightTimer <= 0) s.mode = "run";
      }
    } else {
      // Roam. In a squadron, steer toward the SHARED squad waypoint so the group
      // flies together; solo fighters use their own waypoint.
      let tx: number;
      let ty: number;
      if (s.squad >= 0 && this.squadRoams[s.squad]) {
        const sr = this.squadRoams[s.squad]!;
        if ((sr.x - s.pos.x) ** 2 + (sr.y - s.pos.y) ** 2 < 150 * 150) {
          this.squadRoams[s.squad] = randomWaypoint();
        }
        const cur = this.squadRoams[s.squad]!;
        tx = cur.x;
        ty = cur.y;
      } else {
        if ((s.roam.x - s.pos.x) ** 2 + (s.roam.y - s.pos.y) ** 2 < 90 * 90) {
          s.roam = randomWaypoint();
        }
        tx = s.roam.x;
        ty = s.roam.y;
      }
      desired = Math.atan2(ty - s.pos.y, tx - s.pos.x);
      speed = ENEMY.patrolSpeed;
      s.mode = "run";
    }

    // Blend in a repulsion from nearby rocks so it never flies into them.
    desired = this.avoidRocks(s, ctx.bodies, desired);
    this.steer(s, dt, desired, speed);

    // Turrets are fixed forward — always aligned to the hull nose.
    for (const t of s.turrets) t.angle = s.heading;
    if (firing && dist < ENEMY.turretRange) this.fireForward(s);

    if (s.hitTimer >= ENEMY.regenDelay && s.shield < s.maxShield) {
      s.shield = Math.min(s.maxShield, s.shield + ENEMY.shieldRegenPerSec * dt);
    }
  }

  /** Fire twin red bolts from each live turret's barrels, along the hull heading. */
  private fireForward(s: EnemyShip): void {
    const fx = Math.cos(s.heading);
    const fy = Math.sin(s.heading);
    const px = -fy;
    const py = fx;
    for (let j = 0; j < s.turretCount; j++) {
      const t = s.turrets[j]!;
      if (t.cooldown > 0) continue;
      const off = ENEMY.mounts[j]!;
      const mx = s.pos.x + fx * off.fwd + px * off.lat;
      const my = s.pos.y + fy * off.fwd + py * off.lat;
      for (let k = 0; k < ENEMY.boltsPerShot; k++) {
        const bs = (k * 2 - (ENEMY.boltsPerShot - 1)) * ENEMY.barrelSpread;
        this.bullets.push({
          x: mx + px * bs + fx * ENEMY.muzzleLen,
          y: my + py * bs + fy * ENEMY.muzzleLen,
          vx: fx * ENEMY.boltSpeed,
          vy: fy * ENEMY.boltSpeed,
          life: ENEMY.boltLife,
        });
      }
      t.cooldown = ENEMY.fireInterval;
      t.flash = 1;
    }
  }

  /** Nudge the desired heading away from any asteroid/moon it's about to clip. */
  private avoidRocks(s: EnemyShip, bodies: MineableBody[], desired: number): number {
    let ax = 0;
    let ay = 0;
    for (const b of bodies) {
      if (b.ore <= 0) continue;
      const dx = s.pos.x - b.pos.x;
      const dy = s.pos.y - b.pos.y;
      const rr = b.radius + ENEMY.collisionRadius + ENEMY.avoidRange;
      const d2 = dx * dx + dy * dy;
      if (d2 < rr * rr && d2 > 1) {
        const d = Math.sqrt(d2);
        const w = (rr - d) / rr;
        ax += (dx / d) * w;
        ay += (dy / d) * w;
      }
    }
    if (ax === 0 && ay === 0) return desired;
    const dvx = Math.cos(desired) + ax * 1.7;
    const dvy = Math.sin(desired) + ay * 1.7;
    return Math.atan2(dvy, dvx);
  }

  private steer(s: EnemyShip, dt: number, desired: number, speed: number): void {
    const diff = norm(desired - s.heading);
    const maxTurn = ENEMY.turnRate * dt;
    s.heading += diff > maxTurn ? maxTurn : diff < -maxTurn ? -maxTurn : diff;
    const vx = Math.cos(s.heading) * speed;
    const vy = Math.sin(s.heading) * speed;
    s.vel.set(vx, vy);
    s.pos.x += vx * dt;
    s.pos.y += vy * dt;
  }

  private respawn(s: EnemyShip): void {
    const pos = this.spawnPos();
    s.pos.set(pos.x, pos.y);
    s.vel.set(0, 0);
    s.heading = Math.atan2(-pos.y, -pos.x);
    s.hp = s.maxHp;
    s.shield = s.maxShield;
    s.shieldFlash = 0;
    s.hitTimer = 999;
    s.aggro = false;
    s.mode = "run";
    s.straightTimer = 0;
    s.roam = randomWaypoint();
    s.respawnTimer = 0;
    s.turretCount = Math.random() < 0.5 ? 1 : 2;
    s.warpTarget = null;
    s.holdTimer = 0;
  }
}
